import { useMemo } from "react";

export type UnavailableReason = (value: string) => string | undefined;

export const unavailableUnless = (
  available: readonly string[] | undefined,
  reason: string,
): UnavailableReason | undefined => {
  if (!available) return undefined;
  const availableValues = new Set(available);
  return (value) => (availableValues.has(value) ? undefined : reason);
};

export const useUnavailableReasons = <
  Availability extends Partial<Record<string, readonly string[]>>,
  Key extends keyof Availability & string,
>(
  availability: Availability | undefined,
  reasons: Record<Key, string>,
) =>
  useMemo(() => {
    const keys = Object.keys(reasons) as Key[];
    return Object.fromEntries(keys.map((key) => [key, unavailableUnless(availability?.[key], reasons[key])])) as Record<
      Key,
      UnavailableReason | undefined
    >;
  }, [availability, reasons]);

export const emptyRangeReason = (range: { count?: number } | undefined, reason: string) =>
  range?.count === 0 ? reason : undefined;

export const zeroMaxReason = (max: number | undefined, reason: string) => (max === 0 ? reason : undefined);

export const tabUnavailability = (reason: string | undefined, isActive: boolean) =>
  reason && !isActive ? { disabled: true, disabledTooltip: { title: reason } } : {};

export const CREATOR_UNAVAILABLE_REASONS = {
  riskType: "No videos with this risk found",
  riskTier: "No videos with this severity found",
  hasRisk: "No creators with risks found",
  noRisk: "No creators without risks found",
  openCollaboration: "No creators in open collaborations",
  targetCollaboration: "No creators in target collaborations",
  audienceShare: "No creators with this audience share",
  age: "No creators with this as their top audience age",
  creatorLevel: "No creators at this level",
  categoryPro: "No creators in this category",
  creatorLanguage: "No creators with this language",
  list: "No creators in this list",
  tag: "No creators with this tag",
  cohort: "No creators in this cohort",
  gmv: "No GMV in this date range",
  ctr: "No clicks in this date range",
  ctor: "No orders from clicks in this date range",
  orders: "No orders in this date range",
  aov: "No sales in this date range",
  videosAtRisk: "No creators with videos at risk",
  followers: "No follower data for these creators",
  totalLikes: "No like data for these creators",
  postingFrequency: "No posting data for these creators",
} as const;

export const VIDEO_UNAVAILABLE_REASONS = {
  riskType: "No videos with this risk found",
  riskTier: "No videos with this severity found",
  safetyStatus: "No videos with this status found",
  hasRisk: "No videos with risks found",
  noRisk: "No videos without risks found",
  openCollaboration: "No videos from open collaborations",
  targetCollaboration: "No videos from target collaborations",
  audienceShare: "No videos from creators with this audience share",
  age: "No videos from creators with this as their top audience age",
  creatorLevel: "No videos from creators at this level",
  gmv: "No GMV in this period",
  views: "No views in this period",
  ctr: "No clicks in this period",
  ctor: "No orders from clicks in this period",
  orders: "No orders in this period",
  followers: "No follower data for these creators",
} as const;
